import { useState, useEffect } from 'react';
import { api } from '../../api/client';

interface VoiceConfig {
  voice_enabled: boolean;
  voice_base_url: string;
  mcp_api_key: string;
  quota_by_plan: {
    free: number;
    starter: number;
    pro: number;
    enterprise: number; // -1 = unlimited
  };
}

const DEFAULT_CONFIG: VoiceConfig = {
  voice_enabled: true,
  voice_base_url: 'https://voice.wowerix.com',
  mcp_api_key: '',
  quota_by_plan: {
    free: 100,
    starter: 500,
    pro: 2000,
    enterprise: -1,
  },
};

export function VoiceSettings() {
  const [config, setConfig] = useState<VoiceConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [showKey, setShowKey] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.getVoiceConfig()
      .then(data => setConfig({ ...DEFAULT_CONFIG, ...data }))
      .catch(() => {/* use defaults */})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.saveVoiceConfig(config);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: any) {
      alert('Lưu thất bại: ' + (e.message ?? 'Lỗi không xác định'));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await api.testVoiceConnection(config.voice_base_url, config.mcp_api_key);
      setTestResult({ ok: true, message: result.message ?? `Kết nối thành công! ErixVoice v${result.version ?? '2.0.0'}` });
    } catch (e: any) {
      setTestResult({ ok: false, message: e.message ?? 'Không thể kết nối đến ErixVoice' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '16px', flexDirection: 'column' }}>
        <div className="spinner" style={{ width: 32, height: 32 }} />
        <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Đang tải cấu hình Voice...</span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '0' }}>

      {/* Page Header */}
      <div className="page-header" style={{ flexShrink: 0 }}>
        <div>
          <h1 className="page-title">🎙️ Erix Voice — Cấu Hình Toàn Hệ Thống</h1>
          <p className="page-subtitle">
            Thiết lập kết nối API/MCP với voice.wowerix.com. Cấu hình này áp dụng cho toàn bộ user trong hệ thống.
          </p>
        </div>
        <button
          className="btn btn--primary"
          onClick={handleSave}
          disabled={saving}
          style={{ minWidth: 130 }}
        >
          {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : saved ? '✅ Đã lưu!' : '💾 Lưu Cấu Hình'}
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', overflowY: 'auto', flex: 1 }}>

        {/* Connection Settings */}
        <div className="card">
          <div className="card__title" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <span style={{ fontSize: '20px' }}>🔌</span>
            Kết Nối ErixVoice API
          </div>

          {/* Enable toggle */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: 'var(--bg-glass)', borderRadius: '10px', marginBottom: '16px' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '14px' }}>Kích hoạt Erix Voice</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>Cho phép user trong hệ thống sử dụng tính năng Voice</div>
            </div>
            <button
              onClick={() => setConfig(prev => ({ ...prev, voice_enabled: !prev.voice_enabled }))}
              style={{
                padding: '6px 16px',
                borderRadius: '20px',
                border: 'none',
                background: config.voice_enabled ? 'var(--accent-green)' : 'var(--bg-elevated)',
                color: config.voice_enabled ? '#fff' : 'var(--text-muted)',
                fontWeight: 600,
                fontSize: '13px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {config.voice_enabled ? '✅ ĐANG BẬT' : '⭕ TẮT'}
            </button>
          </div>

          {/* Voice URL */}
          <div style={{ marginBottom: '14px' }}>
            <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
              ErixVoice Base URL
            </label>
            <input
              className="input"
              value={config.voice_base_url}
              onChange={e => setConfig(prev => ({ ...prev, voice_base_url: e.target.value }))}
              placeholder="https://voice.wowerix.com"
              style={{ width: '100%', fontFamily: 'var(--font-mono)', fontSize: '13px' }}
            />
          </div>

          {/* MCP API Key */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
              MCP API Key
              <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginLeft: '8px', fontWeight: 400 }}>
                (Header: X-MCP-API-Key khi gọi POST /v1/mcp)
              </span>
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                className="input"
                type={showKey ? 'text' : 'password'}
                value={config.mcp_api_key}
                onChange={e => setConfig(prev => ({ ...prev, mcp_api_key: e.target.value }))}
                placeholder="erixvoice-mcp-2024"
                style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: '13px' }}
              />
              <button
                className="btn btn--secondary"
                onClick={() => setShowKey(!showKey)}
                style={{ padding: '8px 12px' }}
                title={showKey ? 'Ẩn key' : 'Hiện key'}
              >
                {showKey ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          {/* Test connection */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              className="btn btn--secondary"
              onClick={handleTest}
              disabled={testing || !config.voice_base_url}
            >
              {testing ? <><span className="spinner" style={{ width: 12, height: 12 }} /> Đang kiểm tra...</> : '🔍 Kiểm tra kết nối'}
            </button>
            {testResult && (
              <div style={{
                padding: '6px 14px',
                borderRadius: '8px',
                fontSize: '13px',
                background: testResult.ok ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                border: `1px solid ${testResult.ok ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
                color: testResult.ok ? '#34d399' : '#f87171',
              }}>
                {testResult.ok ? '✅' : '❌'} {testResult.message}
              </div>
            )}
          </div>
        </div>

        {/* Quota by plan */}
        <div className="card">
          <div className="card__title" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <span style={{ fontSize: '20px' }}>📊</span>
            Voice Quota Theo Gói
            <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 400, marginLeft: '4px' }}>
              (requests/tháng per user. -1 = không giới hạn)
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
            {(Object.keys(config.quota_by_plan) as (keyof typeof config.quota_by_plan)[]).map(plan => (
              <div key={plan} style={{ background: 'var(--bg-glass)', borderRadius: '10px', padding: '16px', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'capitalize', fontWeight: 600 }}>
                  {plan === 'enterprise' ? '🏢 Enterprise' : plan === 'pro' ? '⭐ Pro' : plan === 'starter' ? '🚀 Starter' : '🆓 Free'}
                </div>
                <input
                  className="input"
                  type="number"
                  min={-1}
                  value={config.quota_by_plan[plan]}
                  onChange={e => setConfig(prev => ({
                    ...prev,
                    quota_by_plan: { ...prev.quota_by_plan, [plan]: parseInt(e.target.value) || 0 }
                  }))}
                  style={{ width: '100%', fontSize: '20px', fontWeight: 700, textAlign: 'center', padding: '8px' }}
                />
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', textAlign: 'center', marginTop: '4px' }}>
                  {config.quota_by_plan[plan] === -1 ? '∞ Unlimited' : `${config.quota_by_plan[plan]} requests`}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* MCP Tools info */}
        <div className="card" style={{ background: 'rgba(6,182,212,0.04)', border: '1px solid rgba(6,182,212,0.2)' }}>
          <div className="card__title" style={{ color: '#38bdf8', marginBottom: '12px' }}>
            ℹ️ ErixVoice MCP Server Info
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px', color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}>
            <div>GET  {config.voice_base_url || 'https://voice.wowerix.com'}/v1/mcp          → Danh sách MCP tools (public)</div>
            <div>POST {config.voice_base_url || 'https://voice.wowerix.com'}/v1/mcp          → Gọi MCP tool (cần X-MCP-API-Key)</div>
            <div>GET  {config.voice_base_url || 'https://voice.wowerix.com'}/api/care/handshake → Ping kiểm tra kết nối</div>
          </div>
          <div style={{ marginTop: '12px', fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.6 }}>
            <strong>Lưu ý:</strong> MCP Key được lưu an toàn trong Cloudflare KV — không lưu trong database. User thông thường không thể xem được key này.
          </div>
        </div>

      </div>
    </div>
  );
}
